import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { usePermission } from '@/Hooks/usePermission';
import { SpaceColumn } from '@/Pages/Settings/components/spaces/SpaceColumn';
import { SpaceDialog, type SpaceKind } from '@/Pages/Settings/components/spaces/SpaceDialog';
import {
  SpaceDeleteDialog,
  type SpaceDeleteTarget,
} from '@/Pages/Settings/components/spaces/SpaceDeleteDialog';
import type { SpacesChain } from '@/Pages/Settings/components/spaces/useSpacesChain';
import {
  areaDeleteBlock,
  floorDeleteBlock,
  type Area,
  type Floor,
  type VenueTable,
} from '@/Pages/Settings/components/spacesLib';
import { resyncSpacesStructure } from '@/Redux/Slices/spacesApi';
import { useAppDispatch } from '@/Redux/store';

/**
 * The floor, area and table steps — one rung of the spaces chain at a time.
 *
 * Every control here is the Spaces tab's: `SpaceColumn` draws the rung,
 * `SpaceDialog` creates and edits it, `SpaceDeleteDialog` removes it, and the
 * two delete guards come from `spacesLib`. Nothing is re-implemented, because
 * the guards are the part a second implementation loses silently — an area that
 * still holds tables would delete-and-fail with a bare 409 instead of saying so
 * before the click.
 *
 * The chain itself is owned by the wizard, not by this component: the three
 * steps are three views of one hierarchy, and a floor chosen on the area step
 * has to still be chosen on the table step. Mounting the state here would throw
 * it away on every Next.
 */
export function SpacesStep({
  kind,
  chain,
  onWrote,
  onBack,
}: {
  kind: SpaceKind;
  chain: SpacesChain;
  /** The venue changed. Re-probe; do not assume what the probe will say. */
  onWrote: () => void;
  /** Offered when the rung above is empty, because that is the only fix. */
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const dispatch = useAppDispatch();

  const [editing, setEditing] = useState<{ record: Floor | Area | VenueTable | null } | null>(null);
  const [deleting, setDeleting] = useState<SpaceDeleteTarget | null>(null);

  /**
   * Every write here goes through axios, so RTK Query hears nothing.
   * `spacesFloorMap` and `spacesTables` are both cached for an hour and
   * `FloorMap` does not refetch its scenes on mount — so without this a floor
   * added in the wizard was simply absent from /floor-map until the entry
   * expired. The same call the Spaces tab makes, for the same reason.
   */
  const wrote = () => {
    dispatch(resyncSpacesStructure());
    onWrote();
  };

  const reloadRung =
    kind === 'floor' ? chain.loadFloors : kind === 'area' ? chain.loadAreas : chain.loadTables;

  const afterSave = () => {
    reloadRung();
    wrote();
  };

  const removed = (target: SpaceDeleteTarget) => {
    if (target.kind === 'floor' && chain.floorId === target.id) chain.setFloorId(null);
    if (target.kind === 'area' && chain.areaId === target.id) chain.setAreaId(null);
    if (target.kind === 'table' && chain.tableId === target.id) chain.setTableId(null);
    reloadRung();
    wrote();
  };

  const parentId =
    kind === 'floor' ? chain.branchId : kind === 'area' ? (chain.floorId ?? '') : (chain.areaId ?? '');

  /**
   * Whether the rung ABOVE has answered yet, and what it said.
   *
   * The two have to be told apart, because they look identical and mean opposite
   * things: a floor list still in flight and a venue with no floors are both an
   * empty array. Claiming "there is no floor yet" during the first load is a
   * flash of something false, on the one screen whose entire job is telling an
   * operator the truth about what their venue is missing.
   */
  const parentLoading =
    kind === 'floor'
      ? chain.branchesState.loading
      : kind === 'area'
        ? chain.floorsState.loading
        : chain.areasState.loading;

  /**
   * The rung above answered, and it is empty, so this one cannot be created at
   * all — `POST /areas` needs a `floorId` and there is none. Saying "choose a
   * floor" here would be a lie: there is nothing to choose. The way out is one
   * step back, so that is what is offered.
   */
  const parentMissing =
    !parentLoading &&
    (kind === 'floor'
      ? chain.branches.length === 0
      : kind === 'area'
        ? chain.floors.length === 0
        : chain.areas.length === 0);

  /**
   * `POST` at this rung carries the parent's id, so an Add button offered before
   * one is chosen can only produce a 400. `SpaceColumn` draws that as the column
   * waiting, which is also where the load itself is reported.
   */
  const waitingFor = parentLoading
    ? t('common.loading')
    : (kind === 'floor' ? chain.branchId : kind === 'area' ? chain.floorId : chain.areaId)
      ? undefined
      : kind === 'table'
        ? t('setup.table.pickArea')
        : t('setup.area.pickFloor');

  if (parentMissing) {
    const parent = kind === 'floor' ? 'branch' : kind === 'area' ? 'floor' : 'area';
    return (
      <div className="space-y-3">
        <p className="text-sm text-ink-muted">
          {t('setup.wizard.parentMissing', { parent: t(`setup.wizard.parent.${parent}`) })}
        </p>
        <Button variant="outline" size="sm" onClick={onBack}>
          {t('setup.wizard.parentMissingBack', { parent: t(`setup.wizard.parent.${parent}`) })}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Which parent this rung is being added to. A wizard that hid this would
          be asking the operator to add tables to whichever area it happened to
          pick, which on a two-floor venue is a coin toss. */}
      <div className="flex flex-wrap items-end gap-3">
        {kind === 'floor' && chain.branches.length > 0 && (
          <Field label={t('setup.wizard.parent.branch')}>
            <Select
              className="w-56"
              value={chain.branchId}
              onChange={(e) => chain.setBranchId(e.target.value)}
              aria-label={t('setup.wizard.parent.branch')}
            >
              {chain.branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name} · {branch.code}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {(kind === 'area' || kind === 'table') && (
          <Field label={t('setup.wizard.parent.floor')}>
            <Select
              className="w-56"
              value={chain.floorId ?? ''}
              onChange={(e) => chain.setFloorId(e.target.value || null)}
              aria-label={t('setup.wizard.parent.floor')}
            >
              {chain.floors.map((floor) => (
                <option key={floor.id} value={floor.id}>
                  {floor.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {kind === 'table' && (
          <Field label={t('setup.wizard.parent.area')}>
            <Select
              className="w-56"
              value={chain.areaId ?? ''}
              onChange={(e) => chain.setAreaId(e.target.value || null)}
              aria-label={t('setup.wizard.parent.area')}
            >
              {chain.areas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>

      {kind === 'floor' && (
        <Rung
          title={t('setup.floors')}
          waitingFor={waitingFor}
          rows={chain.floors.map((floor) => ({
            id: floor.id,
            title: floor.name,
            subtitle: t('setup.floor.levelValue', { level: floor.level }),
            meta: floor._count ? t('setup.area.count', { count: floor._count.areas }) : undefined,
            muted: !floor.isActive,
            lockedReason: chain.scopeBlock('FLOOR', floor.id),
          }))}
          selectedId={chain.floorId}
          onSelect={chain.setFloorId}
          onCreate={() => setEditing({ record: null })}
          onEdit={(id) => setEditing({ record: chain.floors.find((f) => f.id === id) ?? null })}
          createLabel={t('setup.floor.add')}
          emptyTitle={t('setup.floor.emptyTitle')}
          emptyHint={t('setup.floor.emptyHint')}
          state={chain.floorsState}
          onRetry={chain.loadFloors}
          canCreate={can('floor_map.create')}
          canEdit={can('floor_map.update')}
          canDelete={can('floor_map.delete')}
          createBlockedReason={chain.scopeBlock('BRANCH', chain.branchId)}
          selected={chain.floors.find((f) => f.id === chain.floorId) ?? null}
          deleteLabel={t('setup.floor.delete')}
          deleteBlock={(floor) =>
            chain.scopeBlock('FLOOR', floor.id) ?? floorDeleteBlock(floor, t)
          }
          labelOf={(floor) => floor.name}
          onDelete={(id, label) => setDeleting({ kind: 'floor', id, label })}
        />
      )}

      {kind === 'area' && (
        <Rung
          title={t('setup.areas')}
          waitingFor={waitingFor}
          rows={chain.areas.map((area) => ({
            id: area.id,
            title: area.name,
            subtitle: t(`setup.area.types.${area.type}`, area.type),
            meta: area.capacity ? t('setup.area.seats', { count: area.capacity }) : undefined,
            muted: !area.isActive,
            lockedReason: chain.scopeBlock('AREA', area.id),
          }))}
          selectedId={chain.areaId}
          onSelect={chain.setAreaId}
          onCreate={() => setEditing({ record: null })}
          onEdit={(id) => setEditing({ record: chain.areas.find((a) => a.id === id) ?? null })}
          createLabel={t('setup.area.add')}
          emptyTitle={t('setup.area.emptyTitle')}
          emptyHint={t('setup.area.emptyHint')}
          state={chain.areasState}
          onRetry={chain.loadAreas}
          canCreate={can('floor_map.create')}
          canEdit={can('floor_map.update')}
          canDelete={can('floor_map.delete')}
          createBlockedReason={chain.scopeBlock('FLOOR', chain.floorId)}
          selected={chain.areas.find((a) => a.id === chain.areaId) ?? null}
          deleteLabel={t('setup.area.delete')}
          deleteBlock={(area) => chain.scopeBlock('AREA', area.id) ?? areaDeleteBlock(area, t)}
          labelOf={(area) => area.name}
          onDelete={(id, label) => setDeleting({ kind: 'area', id, label })}
        />
      )}

      {kind === 'table' && (
        <Rung
          title={t('setup.tables')}
          waitingFor={waitingFor}
          rows={chain.tables.map((table) => ({
            id: table.id,
            title: table.number,
            subtitle: table.name ?? undefined,
            meta: t('setup.table.seatCount', { count: table.seats }),
            muted: !table.isActive,
            lockedReason: chain.scopeBlock('TABLE', table.id),
          }))}
          selectedId={chain.tableId}
          onSelect={chain.setTableId}
          onCreate={() => setEditing({ record: null })}
          onEdit={(id) => setEditing({ record: chain.tables.find((tb) => tb.id === id) ?? null })}
          createLabel={t('setup.table.add')}
          emptyTitle={t('setup.table.emptyTitle')}
          emptyHint={t('setup.table.emptyHint')}
          state={chain.tablesState}
          onRetry={chain.loadTables}
          canCreate={can('tables.create')}
          canEdit={can('tables.update')}
          canDelete={can('tables.delete')}
          createBlockedReason={chain.scopeBlock('AREA', chain.areaId)}
          selected={chain.tables.find((tb) => tb.id === chain.tableId) ?? null}
          deleteLabel={t('setup.table.delete')}
          // No child guard: `DELETE /tables/:id` detaches sessions, reservations
          // and service requests (`onDelete: SetNull`) rather than refusing, so
          // scope is the only thing that can stand in the way.
          deleteBlock={(table) => chain.scopeBlock('TABLE', table.id)}
          labelOf={(table) => table.number}
          onDelete={(id, label) => setDeleting({ kind: 'table', id, label })}
        />
      )}

      {editing && (
        <SpaceDialog
          kind={kind}
          open
          record={editing.record}
          parentId={parentId}
          onClose={() => setEditing(null)}
          onSaved={afterSave}
        />
      )}

      <SpaceDeleteDialog target={deleting} onClose={() => setDeleting(null)} onDeleted={removed} />
    </div>
  );
}

interface RungRow {
  id: string;
  title: string;
  subtitle?: string;
  meta?: string;
  muted?: boolean;
  lockedReason?: string | null;
}

/**
 * `SpaceColumn` plus the delete button that belongs under it.
 *
 * The Spaces tab draws three of these side by side and puts the delete in each
 * column's footer; a wizard shows one at a time, so the shape is the same and
 * only the arrangement differs. Kept generic over the row type so the three
 * call sites above stay readable rather than collapsing into one branchy blob.
 */
function Rung<T extends { id: string }>({
  title,
  waitingFor,
  rows,
  selectedId,
  onSelect,
  onCreate,
  onEdit,
  createLabel,
  emptyTitle,
  emptyHint,
  state,
  onRetry,
  canCreate,
  canEdit,
  canDelete,
  createBlockedReason,
  selected,
  deleteLabel,
  deleteBlock,
  labelOf,
  onDelete,
}: {
  title: string;
  waitingFor?: string;
  rows: RungRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onEdit: (id: string) => void;
  createLabel: string;
  emptyTitle: string;
  emptyHint: string;
  state: { loading: boolean; error: string | null };
  onRetry: () => void;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  createBlockedReason?: string | null;
  selected: T | null;
  deleteLabel: string;
  deleteBlock: (row: T) => string | null;
  labelOf: (row: T) => string;
  onDelete: (id: string, label: string) => void;
}) {
  const block = selected ? deleteBlock(selected) : null;
  return (
    <SpaceColumn
      title={title}
      waitingFor={waitingFor}
      rows={rows}
      selectedId={selectedId}
      onSelect={onSelect}
      onCreate={onCreate}
      onEdit={onEdit}
      createLabel={createLabel}
      emptyTitle={emptyTitle}
      emptyHint={emptyHint}
      loading={state.loading}
      error={state.error}
      onRetry={onRetry}
      canCreate={canCreate}
      canEdit={canEdit}
      createBlockedReason={createBlockedReason}
      footer={
        selected && canDelete ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={Boolean(block)}
            title={block ?? undefined}
            onClick={() => onDelete(selected.id, labelOf(selected))}
          >
            <Icon as={Trash2} aria-hidden />
            {block ?? deleteLabel}
          </Button>
        ) : undefined
      }
    />
  );
}
